// src/services/gameGenerator.js
//
// Builds game content from a song's lyrics — no AI, no network, no cost.
// Pure functions: give it a topic, get back every game type the lyrics can
// support. The Game generator panel (pages/builder/GameGeneratorPanel.js)
// shows, edits and publishes the result.
//
// WHAT IT MAKES
// -------------
//   mc        Multiple Choice questions. Also feeds Talk Choice, Typing Quiz
//             and Class Quiz Show, which read the same shape.
//             { question, questionImage?, answers: [{ text, correct }] }
//             Kinds, best first:
//               definition  "A push or a pull to an object. It's what we
//                            call force!"  ->  "What is a push or a pull to
//                            an object called?"  (force)
//               fill        "Finish the lyric: If an object changes its ___"
//               next        "What comes next in the song?"
//               inSong      "Which word is in the song?"
//   dragDrop  Drag each word onto the lyric it finishes.
//             { question, options: [{ id, text, image?, targetText }] }
//   sorting   Sorting Conveyor: "In the song" vs "Not in the song" bins.
//             { question, options: [{ id, text, image?, targetText }] }
//   memory    Memory Match: each word paired with its picture (or the two
//             halves of a lyric line when there aren't enough pictures).
//             [{ question, options: [{ id, text, image?, targetText, targetImage? }] }]
//   match3    Match Three tiles made from the song's pictures.
//             [{ prompt, tileTypes: [emoji, ...] }]
//
// Shapes follow what each game component actually reads (see the comments
// in config/gameRegistry.js and the game files themselves).
//
// SAME INPUT, SAME OUTPUT
// -----------------------
// Everything random comes from a seeded generator, so a draft can be
// reproduced from (lyrics, seed, count). "Regenerate" just picks a new seed.

import { DEFAULT_TILES, WORD_BANKS, emojiFor, emojiImage } from "./gameGeneratorEmoji";

/* ------------------------------------------------------------------ */
/* Seeded randomness                                                   */
/* ------------------------------------------------------------------ */

export const hashString = (str) => {
  let h = 2166136261;
  const s = String(str);
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const rngFrom = (seed) => {
  let a = hashString(seed) || 1;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const shuffle = (arr, rand) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/* ------------------------------------------------------------------ */
/* Reading the lyrics                                                  */
/* ------------------------------------------------------------------ */
//
// Same rules as lyricSyncService.js: "(Chorus)" / "[verse 2]" headings and
// the "© Smart Board Learning…" credit are not sung; blank rows split
// sections. Kept here as plain functions so this file has no Firebase
// imports and can run anywhere.

const HEADER_RE = /^\s*[([]\s*([^)\]]{1,40}?)\s*[)\]]\s*:?\s*$/;
const CREDIT_RE = /^\s*(©|\(c\)\s|copyright\b)/i;

export const toLines = (lyrics) =>
  Array.isArray(lyrics)
    ? lyrics.map((l) => String(l ?? ""))
    : typeof lyrics === "string"
    ? lyrics.split("\n")
    : [];

const isSung = (t) => {
  const s = String(t || "").trim();
  return s !== "" && !HEADER_RE.test(s) && !CREDIT_RE.test(s);
};

const clean = (s) =>
  String(s || "")
    .replace(/[‘’`]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, " ")
    .trim();

const stripEndPunct = (s) => clean(s).replace(/[\s.,!?;:]+$/, "");
const lowerFirst = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
const upperFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const norm = (s) => clean(s).toLowerCase().replace(/[^a-z0-9 ]/g, "").trim();

/** Stable fingerprint of the sung lyrics; the panel uses it to spot edits. */
export const lyricsHashOf = (lyrics) =>
  hashString(toLines(lyrics).filter(isSung).map(norm).join("|")).toString(36);

const STOP = new Set(
  (
    "a an the and or but if then than so to of in on at by for from with as is are was were be been being " +
    "it its it's this that these those there their they them we our you your he she his her i me my mine " +
    "what when where who why how which while into onto out up down over under again very just also too " +
    "all any some each every no not yes can can't will won't would should could do does did done don't " +
    "have has had get got go goes going gonna wanna let let's make makes made say says said see look " +
    "like know way thing things one two lot lots much more most many come comes came put take takes " +
    "yeah oh ooh hey uh la na whoa baby alright okay ok now here well back about around through call called " +
    "use used using us am im i'm you're we're they're that's what's isn't aren't doesn't didn't " +
    "your yours ours ourselves myself yourself everybody everyone somebody someone something nothing " +
    "time times day days really right left keep kept give gave need needs want wants tell told show " +
    "same other another next first last new old big small little long"
  ).split(/\s+/)
);

const WORD_RE = /[A-Za-z][A-Za-z'-]*[A-Za-z]|[A-Za-z]/g;

const isContent = (w) => {
  const lw = w.toLowerCase();
  if (STOP.has(lw) || STOP.has(lw.replace(/'s$/, ""))) return false;
  if (lw.length >= 4) return true;
  return lw.length === 3 && Boolean(emojiFor(lw));
};

export const analyzeLyrics = (lyrics, title = "") => {
  const lines = toLines(lyrics);
  const sung = lines.map(clean).filter(isSung);

  // First-seen order, choruses counted once.
  const unique = [];
  const seen = new Set();
  sung.forEach((l) => {
    const k = norm(l);
    if (k && !seen.has(k)) {
      seen.add(k);
      unique.push(l);
    }
  });

  const freq = {};
  const display = {};
  sung.forEach((l) => {
    (l.match(WORD_RE) || []).forEach((w) => {
      if (!isContent(w)) return;
      const k = w.toLowerCase().replace(/'s$/, "");
      freq[k] = (freq[k] || 0) + 1;
      // Keep capitals only for names (capitalised mid-line).
      if (!display[k]) display[k] = k;
    });
    (l.match(WORD_RE) || []).forEach((w, idx) => {
      const k = w.toLowerCase().replace(/'s$/, "");
      if (idx > 0 && /^[A-Z]/.test(w) && freq[k]) display[k] = w.replace(/'s$/, "");
    });
  });

  // Fold plurals into their singular when both appear ("solids" + "solid"),
  // so one idea never shows up twice as two cards or two answers.
  Object.keys(freq).forEach((k) => {
    const single = k.endsWith("es") && freq[k.slice(0, -2)] ? k.slice(0, -2) : k.endsWith("s") && freq[k.slice(0, -1)] ? k.slice(0, -1) : "";
    if (single && single !== k) {
      freq[single] += freq[k];
      delete freq[k];
    }
  });

  const titleWords = new Set((String(title).match(WORD_RE) || []).map((w) => w.toLowerCase()));
  const score = (k) =>
    freq[k] + (titleWords.has(k) ? 3 : 0) + (emojiFor(k) ? 1 : 0) + Math.min(k.length, 10) / 10;

  const keywords = Object.keys(freq).sort((a, b) => score(b) - score(a));
  const inSong = new Set(Object.keys(freq));
  sung.forEach((l) => (l.match(WORD_RE) || []).forEach((w) => inSong.add(w.toLowerCase())));

  return { lines, sung, unique, keywords, freq, display, inSong, titleWords };
};

/* ------------------------------------------------------------------ */
/* Wrong answers                                                       */
/* ------------------------------------------------------------------ */

const suffixClass = (w) => {
  const s = w.toLowerCase();
  if (s.endsWith("ing")) return "ing";
  if (s.endsWith("ed")) return "ed";
  if (s.endsWith("ly")) return "ly";
  if (s.endsWith("tion") || s.endsWith("sion")) return "tion";
  if (s.endsWith("s") && !s.endsWith("ss")) return "s";
  return "";
};

/** Believable wrong words for `answer`: same ending, similar length. */
const wordDistractors = ({ answer, avoidText, pool, bank, rand, n }) => {
  const a = answer.toLowerCase();
  const avoid = norm(avoidText);
  const cls = suffixClass(a);
  const rank = (w) => {
    let r = rand() * 1.5;
    if (suffixClass(w) === cls) r += 2;
    if (Math.abs(w.length - a.length) <= 3) r += 1;
    return r;
  };
  const ok = (w) => {
    const lw = w.toLowerCase();
    return (
      lw !== a &&
      !lw.startsWith(a) &&
      !a.startsWith(lw) &&
      !new RegExp(`\\b${lw.replace(/[^a-z0-9]/g, "")}\\b`).test(avoid)
    );
  };
  const fromSong = pool.filter(ok).sort((x, y) => rank(y) - rank(x));
  const fromBank = bank.filter((w) => ok(w) && !fromSong.includes(w)).sort((x, y) => rank(y) - rank(x));
  const out = [];
  [...fromSong, ...fromBank].forEach((w) => {
    if (out.length < n && !out.some((o) => o.toLowerCase() === w.toLowerCase())) out.push(w);
  });
  return out;
};

/* ------------------------------------------------------------------ */
/* Multiple choice candidates                                          */
/* ------------------------------------------------------------------ */

const DEF_CALL_RE =
  /^(?:and\s+)?(?:it'?s|that'?s|this\s+is|that\s+is|it\s+is|these\s+are|they'?re)\s+(?:what\s+)?(?:we|you|they|people|scientists)\s+call(?:ed)?\s+(?:it\s+)?(?:a\s+|an\s+|the\s+)?([a-z][a-z' -]{1,30}?)[\s.!?,]*$/i;
const CALLED_RE =
  /^(?:a\s+|an\s+|the\s+)?(.{3,45}?)\s+(is|are)\s+called\s+(?:a\s+|an\s+|the\s+)?([a-z][a-z' -]{1,30}?)[\s.!?,]*$/i;
const IS_RE =
  /^(?:a\s+|an\s+|the\s+)?([a-z][a-z' -]{2,25}?)\s+(is|are|means)\s+((?:a|an|the|when|where|how|what|something|anything|made|part|one|all)\b.{4,60}?)[\s.!?,]*$/i;

const makeIcon = (words, avoid = []) => {
  const banned = new Set(avoid.map((w) => w.toLowerCase()));
  const hit = words.find((w) => !banned.has(w.toLowerCase()) && emojiFor(w));
  return hit ? emojiFor(hit) : "";
};

const mcCandidates = (A, { subject, title, rand }) => {
  const bank = WORD_BANKS[subject] || WORD_BANKS.other;
  const pool = A.keywords.map((k) => A.display[k] || k);
  const out = [];
  // The title's picture is the fallback icon — but never when the answer is
  // a title word ("Sound Vibration" -> 🔊 above a question whose answer is
  // "sound" gives it away).
  const titleWordsList = [...(String(title).match(WORD_RE) || [])];
  const titleIcon = makeIcon(titleWordsList);
  const titleIconAvoiding = (answer) => {
    const a = String(answer || "").toLowerCase();
    const icon = makeIcon(titleWordsList, [a]);
    const answerIcon = emojiFor(a);
    return icon && icon !== answerIcon ? icon : "";
  };

  // ---- definitions ----
  const terms = [];
  const defs = [];
  A.unique.forEach((line, i) => {
    let m = line.match(DEF_CALL_RE);
    if (m && i > 0) {
      const clue = stripEndPunct(A.unique[i - 1]);
      const term = stripEndPunct(m[1]);
      if (clue.split(" ").length >= 3 && term.split(" ").length <= 3) {
        defs.push({ kind: "definition", clue, term, line: `${A.unique[i - 1]} ${line}` });
        terms.push(term);
      }
      return;
    }
    m = line.match(CALLED_RE);
    if (m) {
      const subj = stripEndPunct(m[1]);
      const term = stripEndPunct(m[3]);
      if (term.split(" ").length <= 3) {
        defs.push({
          kind: "definition",
          question: `What ${m[2].toLowerCase()} ${lowerFirst(subj)} called?`,
          term,
          line,
        });
        terms.push(term);
      }
    }
  });
  defs.forEach((d) => {
    const question = d.question || `What is ${lowerFirst(d.clue)} called?`;
    out.push({
      kind: "definition",
      question,
      correct: d.term,
      wrongPool: [...terms.filter((t) => t !== d.term)],
      avoidText: d.line,
      icon: makeIcon(d.line.match(WORD_RE) || [], [d.term]) || titleIconAvoiding(d.term),
      score: 10,
    });
  });

  // "X is a ..." statements: only when there are at least 3, so the wrong
  // answers can be the other statements' endings.
  const isStmts = [];
  A.unique.forEach((line) => {
    const m = line.match(IS_RE);
    if (!m) return;
    const subj = stripEndPunct(m[1]);
    if (subj.split(" ").length > 3 || STOP.has(subj.toLowerCase())) return;
    isStmts.push({ subj, verb: m[2].toLowerCase(), pred: stripEndPunct(m[3]), line });
  });
  if (isStmts.length >= 3) {
    isStmts.forEach((s) => {
      out.push({
        kind: "definition",
        question: `In the song, what ${s.verb === "means" ? "does" : s.verb} ${lowerFirst(s.subj)}${
          s.verb === "means" ? " mean" : ""
        }?`,
        correct: upperFirst(s.pred),
        wrongPool: isStmts.filter((o) => o !== s).map((o) => upperFirst(o.pred)),
        phrase: true,
        avoidText: "",
        icon: makeIcon([s.subj]) || titleIcon,
        score: 8,
      });
    });
  }

  // ---- fill in the blank ----
  const usedAnswers = new Set(terms.map((t) => t.toLowerCase()));
  A.unique.forEach((line) => {
    const words = line.match(WORD_RE) || [];
    if (words.length < 4) return;
    const choices = words
      .filter(isContent)
      .map((w) => w.replace(/'s$/, ""))
      .sort((x, y) => (A.freq[y.toLowerCase()] || 0) - (A.freq[x.toLowerCase()] || 0) || y.length - x.length);
    // The word must appear exactly once in the line — otherwise the other
    // copy gives the answer away ("It's from the ___, it's from the vibration").
    const onceIn = (w) =>
      (line.match(new RegExp(`\\b${w.replace(/[-']/g, "\\$&")}\\b`, "gi")) || []).length === 1;
    const usable = choices.filter(onceIn);
    const fresh = usable.find((w) => !usedAnswers.has(w.toLowerCase()));
    const pick = fresh || usable[0];
    if (!pick) return;
    // Same answer as an earlier question: keep it only as a spare.
    const repeat = !fresh;
    const re = new RegExp(`\\b${pick.replace(/[-']/g, "\\$&")}\\b`, "i");
    if (!re.test(line)) return;
    const blanked = stripEndPunct(line.replace(re, "_____"));
    usedAnswers.add(pick.toLowerCase());
    const atStart = blanked.startsWith("_____");
    const shown = A.display[pick.toLowerCase()] || pick.toLowerCase();
    out.push({
      kind: "fill",
      question: `Finish the lyric: “${blanked}”`,
      correct: atStart ? upperFirst(shown) : shown,
      line,
      blanked,
      word: pick,
      wrongPool: pool,
      avoidText: line,
      icon: (() => {
        const own = makeIcon(words, [pick]);
        return own && own !== emojiFor(pick) ? own : titleIconAvoiding(pick);
      })(),
      score: (repeat ? 2 : 6) + Math.min(3, A.freq[pick.toLowerCase()] || 0) * 0.5,
    });
  });

  // ---- what comes next ----
  if (A.unique.length >= 4) {
    A.unique.slice(0, -1).forEach((line, i) => {
      const next = A.unique[i + 1];
      if (line.split(" ").length < 3 || next.split(" ").length < 3) return;
      out.push({
        kind: "next",
        question: `What comes next in the song? “${stripEndPunct(line)} …”`,
        correct: stripEndPunct(next),
        wrongPool: A.unique.filter((l, j) => j !== i && j !== i + 1).map(stripEndPunct),
        phrase: true,
        avoidText: "",
        icon: titleIcon,
        score: 4,
      });
    });
  }

  // ---- which word is in the song ----
  const notInSong = bank.filter((w) => !A.inSong.has(w.toLowerCase()));
  const strongWords = [...A.keywords.filter((k) => emojiFor(k)), ...A.keywords.filter((k) => !emojiFor(k))];
  strongWords.slice(0, 3).forEach((k) => {
    if (notInSong.length < 3) return;
    out.push({
      kind: "inSong",
      question: "Which word is in the song?",
      correct: A.display[k] || k,
      wrongPool: notInSong,
      bankOnly: true,
      avoidText: "",
      icon: titleIconAvoiding(k),
      score: 3,
    });
  });

  return { candidates: out, pool, bank, notInSong };
};

/** Turns a candidate into a finished question with 2 or 3 wrong answers. */
const finishMc = (c, { pool, bank, rand, wrongCount }) => {
  let wrong;
  if (c.phrase) {
    wrong = shuffle(
      c.wrongPool.filter((w) => norm(w) !== norm(c.correct)),
      rand
    ).slice(0, wrongCount);
  } else if (c.bankOnly) {
    wrong = shuffle(c.wrongPool, rand).slice(0, wrongCount);
  } else {
    wrong = wordDistractors({
      answer: c.correct,
      avoidText: c.avoidText,
      pool: [...c.wrongPool, ...pool],
      bank,
      rand,
      n: wrongCount,
    });
  }
  // Every choice gets the right answer's capital-letter style, so a lone
  // capital ("West" among "tribe", "map") can't give the answer away.
  if (!c.phrase) {
    const cap = /^[A-Z]/.test(c.correct);
    wrong = wrong.map((w) => (cap ? upperFirst(w) : w.toLowerCase()));
  }
  // Dedupe against the right answer and each other.
  const uniq = [];
  wrong.forEach((w) => {
    if (norm(w) !== norm(c.correct) && !uniq.some((u) => norm(u) === norm(w))) uniq.push(w);
  });
  if (uniq.length < 2) return null;
  const answers = shuffle(
    [{ text: c.correct, correct: true }, ...uniq.map((text) => ({ text, correct: false }))],
    rand
  );
  const q = { question: c.question, answers, kind: c.kind };
  if (c.icon) q.questionImage = emojiImage(c.icon);
  return q;
};

/* ------------------------------------------------------------------ */
/* The other games                                                     */
/* ------------------------------------------------------------------ */

const optionId = (prefix, i) => `${prefix}${i + 1}`;

const buildDragDrop = (fills, count, rand) => {
  const picks = shuffle(fills, rand).slice(0, Math.max(3, Math.min(8, count)));
  if (picks.length < 3) return [];
  const groups = picks.length >= 6 ? [picks.slice(0, Math.ceil(picks.length / 2)), picks.slice(Math.ceil(picks.length / 2))] : [picks];
  return groups.map((g, gi) => ({
    question: "Drag each word to the lyric it finishes.",
    options: g.map((c, i) => {
      const o = { id: optionId(`d${gi + 1}-`, i), text: c.correct, targetText: c.blanked };
      const icon = emojiFor(c.word);
      if (icon) o.image = emojiImage(icon);
      return o;
    }),
  }));
};

// Label It! — the song's strongest words as tiles, laid out on a loose grid
// until the admin adds a picture and drags each spot into place.
const LABEL_SPOT = (n) => ({ x: 14 + (n % 5) * 18, y: 16 + (Math.floor(n / 5) % 4) * 22 });
const capFirst = (w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w);
// Words that make poor labels on a picture (counting words, directions on
// their own, filler).
const NOT_LABELS = new Set(
  "one two three four five six seven eight nine ten first last north south east west left right up down big small many some every each all about around know like just".split(" ")
);
const buildLabelIt = (A, title, count) => {
  const words = [];
  const add = (k) => {
    const w = String(A.display[k] || k).trim();
    if (w.length > 2 && !NOT_LABELS.has(w.toLowerCase()) && !words.some((x) => x.toLowerCase() === w.toLowerCase())) words.push(w);
  };
  // Names first (Africa, Pacific, Mercury…: sung with a capital letter
  // mid-line) — they're what a picture gets labelled with — then the song's
  // other strongest words.
  A.keywords.filter((k) => /^[A-Z]/.test(String(A.display[k] || ""))).forEach(add);
  A.keywords.forEach(add);
  const pick = words.slice(0, Math.max(3, Math.min(8, count)));
  if (pick.length < 2) return [];
  return [
    {
      question: "Drag each word to its place on the picture!",
      image: "",
      imageAlt: "",
      // what the 🎨 AI picture box starts with (not published)
      idea: `${title}, clearly showing: ${pick.join(", ")}`,
      tiles: pick.map((w, i) => ({ id: `l${i + 1}`, text: capFirst(w), ...LABEL_SPOT(i) })),
    },
  ];
};

/** Label It! starting board for a topic — for drafts made before Label It! existed. */
export const labelItFor = (topic, count = 6) => {
  const A = analyzeLyrics(topic?.lyrics, topic?.title || "");
  if (A.unique.length < 2) return [];
  return buildLabelIt(A, topic?.title || topic?.slug || "", count);
};

const IN_BIN = "🎵 In the song";
const OUT_BIN = "🚫 Not in the song";

const buildSorting = (A, notInSong, count, rand) => {
  const per = Math.max(3, Math.min(5, Math.ceil(count / 2)));
  const ins = A.keywords.slice(0, 12).map((k) => A.display[k] || k);
  const inPick = shuffle(ins, rand).slice(0, per);
  const outPick = shuffle(notInSong, rand).slice(0, per);
  if (inPick.length < 3 || outPick.length < 3) return [];
  const items = shuffle(
    [
      ...inPick.map((text) => ({ text, targetText: IN_BIN })),
      ...outPick.map((text) => ({ text, targetText: OUT_BIN })),
    ],
    rand
  ).map((o, i) => {
    const icon = emojiFor(o.text);
    return { id: optionId("s", i), ...o, ...(icon ? { image: emojiImage(icon) } : {}) };
  });
  return [{ question: "Was this word in the song? Sort each one.", options: items }];
};

const buildMemory = (A, count, rand) => {
  const n = Math.max(3, Math.min(6, count));
  // One picture per pair — two words with the same icon would make two
  // "right" matches for one card.
  const usedIcons = new Set();
  const withIcons = A.keywords.filter((k) => {
    const e = emojiFor(k);
    if (!e || usedIcons.has(e)) return false;
    usedIcons.add(e);
    return true;
  });
  if (withIcons.length >= 3) {
    const picks = shuffle(withIcons.slice(0, 10), rand).slice(0, n);
    return [
      {
        question: "Match each word to its picture.",
        options: picks.map((k, i) => ({
          id: optionId("m", i),
          text: A.display[k] || k,
          targetText: "",
          targetImage: emojiImage(emojiFor(k)),
        })),
      },
    ];
  }
  // Not enough pictures: match the start of a line to its end.
  const halves = A.unique
    .map((l) => stripEndPunct(l).split(" "))
    .filter((w) => w.length >= 6)
    .map((w) => [w.slice(0, Math.ceil(w.length / 2)).join(" "), w.slice(Math.ceil(w.length / 2)).join(" ")]);
  const picks = shuffle(halves, rand).slice(0, n);
  if (picks.length < 3) return [];
  return [
    {
      question: "Match the start of each lyric to its ending.",
      options: picks.map(([a, b], i) => ({ id: optionId("m", i), text: `${a} …`, targetText: `… ${b}` })),
    },
  ];
};

const buildMatch3 = (A, title) => {
  const tiles = [];
  A.keywords.forEach((k) => {
    const e = emojiFor(k);
    if (e && !tiles.includes(e) && tiles.length < 6) tiles.push(e);
  });
  const usedSong = tiles.length >= 3;
  const tileTypes = usedSong ? tiles : DEFAULT_TILES;
  return [{ prompt: `Match three ${title ? `${title} ` : ""}pictures in a row!`, tileTypes, fromSong: usedSong }];
};

/* ------------------------------------------------------------------ */
/* Game types and bonus rounds                                         */
/* ------------------------------------------------------------------ */

// gameType -> which generated content it plays.
export const GAME_TYPES = [
  { value: "multipleChoice", label: "Multiple Choice", family: "mc", group: "Question games" },
  { value: "talkChoice", label: "Talk Choice", family: "mc", group: "Question games" },
  { value: "typingQuiz", label: "Typing Quiz", family: "mc", group: "Question games" },
  { value: "classQuizShow", label: "Class Quiz Show", family: "mc", group: "Question games" },
  { value: "dragDrop", label: "Drag and Drop", family: "dragDrop", group: "Lyric matching games" },
  { value: "sortingConveyor", label: "Sorting Conveyor", family: "sorting", group: "Lyric matching games" },
  { value: "memoryMatch", label: "Memory Match", family: "memory", group: "Lyric matching games" },
  { value: "match3", label: "Match Three", family: "match3", group: "Picture game" },
  // Needs a picture and drop spots placed by hand, so the generator only
  // starts it: tiles from the song's words, plus a suggestion for what the
  // AI picture should show. Never picked as a draft's first game type.
  { value: "labelIt", label: "Label It!", family: "labelIt", group: "Picture game" },
];

export const familyOf = (gameType) => GAME_TYPES.find((g) => g.value === gameType)?.family || "mc";

// Bonus rounds that work with generated content. memoryMatch and countPop
// are left out on purpose: memoryMatch dead-ends on question data and
// countPop always reports a bonus score of 0 (see BonusRouter /
// UniversalQuizGame). typingQuiz reads multiple-choice answers, so it is
// only offered after a question game. puzzleShuffle needs a picture, so it
// is only offered when the topic has one.
export const BONUS_OPTIONS = [
  { value: "diamondTap", label: "Diamond Tap" },
  { value: "match3", label: "Match Three" },
  { value: "rhythmTap", label: "Rhythm Tap" },
  { value: "puzzleShuffle", label: "Puzzle Shuffle", needsImage: true },
  { value: "typingQuiz", label: "Typing Quiz", needsMc: true },
  { value: "none", label: "No bonus round" },
];

export const bonusOptionsFor = (gameType, hasImage) =>
  BONUS_OPTIONS.filter(
    (b) => (!b.needsImage || hasImage) && (!b.needsMc || familyOf(gameType) === "mc")
  );

/** A different bonus round for different topics, and each regenerate. */
export const pickBonus = (slug, seed, gameType, hasImage) => {
  const opts = bonusOptionsFor(gameType, hasImage).filter((b) => b.value !== "none");
  return opts[hashString(`${slug}|${seed}|bonus`) % opts.length].value;
};

/* ------------------------------------------------------------------ */
/* Main entry                                                          */
/* ------------------------------------------------------------------ */

/**
 * @param topic  { slug, title, lyrics, subject }
 * @param opts   { seed?, count? }  count = questions per game, 5–8
 * @returns {
 *   seed, count, lyricsHash,
 *   content: { mc, dragDrop, sorting, memory, match3, labelIt },
 *   spare: [mc questions not used — for "swap this question"],
 *   available: { [gameType]: boolean },
 *   notes: [strings explaining anything that couldn't be made]
 * }
 */
export const generateGames = (topic, opts = {}) => {
  const slug = topic.slug || "";
  const seed = opts.seed || `${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;
  const rand = rngFrom(`${slug}|${seed}`);
  const count = Math.max(5, Math.min(8, opts.count || 5 + Math.floor(rand() * 4)));
  const subject = WORD_BANKS[topic.subject] ? topic.subject : "other";

  const A = analyzeLyrics(topic.lyrics, topic.title);
  const notes = [];

  if (A.unique.length < 2) {
    return {
      seed,
      count,
      lyricsHash: lyricsHashOf(topic.lyrics),
      content: { mc: [], dragDrop: [], sorting: [], memory: [], match3: [], labelIt: [] },
      spare: [],
      available: Object.fromEntries(GAME_TYPES.map((g) => [g.value, false])),
      notes: ["This topic has too few lyric lines to make games from."],
    };
  }

  const { candidates, pool, bank, notInSong } = mcCandidates(A, { subject, title: topic.title, rand });

  // Order: definitions first, then a mix. At most one "next line" and one
  // "which word" question, so a game is mostly about meaning and words.
  const byKind = (k) => shuffle(candidates.filter((c) => c.kind === k), rand).sort((a, b) => b.score - a.score);
  const defsList = byKind("definition");
  const fillList = byKind("fill");
  const firstFills = Math.max(1, count - 2 - defsList.length);
  const ordered = [
    ...defsList,
    ...fillList.slice(0, firstFills),
    ...byKind("next").slice(0, 1),
    ...byKind("inSong").slice(0, 1),
    ...fillList.slice(firstFills),
    ...byKind("next").slice(1, 3),
  ];
  const mc = [];
  const spare = [];
  const seenQ = new Set();
  ordered.forEach((c) => {
    const key = norm(c.question) + "|" + norm(c.correct);
    if (seenQ.has(key)) return;
    seenQ.add(key);
    const wrongCount = rand() < 0.6 ? 3 : 2;
    const q = finishMc(c, { pool, bank, rand, wrongCount });
    if (!q) return;
    if (mc.length < count) mc.push(q);
    else spare.push(q);
  });
  if (mc.length < 5) notes.push(`Only ${mc.length} good questions could be made from these lyrics.`);

  const fills = candidates.filter((c) => c.kind === "fill");
  const dragDrop = buildDragDrop(fills, count, rand);
  const sorting = buildSorting(A, notInSong, count, rand);
  const memory = buildMemory(A, count, rand);
  const match3 = buildMatch3(A, topic.title);
  if (!match3[0].fromSong) notes.push("Match Three uses general pictures — the song has few words with icons.");

  const labelIt = buildLabelIt(A, topic.title || slug, count);

  const content = { mc, dragDrop, sorting, memory, match3, labelIt };
  const available = Object.fromEntries(
    GAME_TYPES.map((g) => [g.value, g.family === "mc" ? mc.length >= 3 : (content[g.family] || []).length > 0])
  );

  return { seed, count, lyricsHash: lyricsHashOf(topic.lyrics), content, spare, available, notes };
};

/**
 * What gets written to quizzes/{slug} on publish.
 *
 * Bonus rounds read their settings from "questions[0]", and the game
 * shuffles questions first — so the bonus settings go on EVERY question
 * (tileTypes for Match Three, noteCount for Rhythm Tap, size for Puzzle
 * Shuffle). The games ignore keys they don't use.
 */
export const questionsForPublish = (content, gameType) => {
  const family = familyOf(gameType);
  const tiles = content.match3?.[0]?.tileTypes || DEFAULT_TILES;
  const base = (content[family] || []).map((q) => {
    // `idea` is the Label It! picture suggestion — editor only
    const { kind, fromSong, idea, ...rest } = q;
    return rest;
  });
  return base.map((q) => ({ tileTypes: tiles, noteCount: 28, size: 3, ...q }));
};
